import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import useAuth from '@/hooks/useAuth'

import EmailInput from '@components/auth/EmailInput/EmailInput'
import PasswordInput from '@components/auth/PasswordInput/PasswordInput'
import Button from '@components/common/Button/Button'
import Logo from '@components/common/Logo/Logo'

import MotionText from '@/components/common/Motion/MotionText'
import { usePageSetup } from '@/hooks/usePageSetup'

export const UserLoginPage = () => {
  const { t } = useTranslation()

  usePageSetup(t('auth.pageTitle.login'), { scroll: true, noindex: true })

  const { login, isLoading, error } = useAuth()

  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const email = formData.get('email') as string
    const password = formData.get('password') as string

    try {
      const response = await login({
        session: {
          email: email,
          password: password
        }
      })

      const user = response?.user

      /*
        When we have user object returned the session created successfully.
        Otherwise we show Login with MFA.

        After login or MFA it should load dashboard data before rendering?
        Want to keep good user experience to not appear that money is missing.
      */
      if (user) {
        if (user.mfa) {
          navigate("/")
        } else {
          navigate("/kyc/intro")
        }
      } else {
        navigate("/users/mfa", { state: { email, password } })
      }
    } catch (error: any) { }
  }

  return (
    <form onSubmit={handleSubmit} className="h-full bg-white p-5 pt-8 pb-8 flex flex-col">
      <div
        onClick={() => navigate('/')}
        className="space-y-4">
        <Logo />
      </div>

      <div className="flex-1 flex flex-col">
        <div className='h-[2rem] w-1'></div>
        <div className="space-y-4">
          <MotionText
            as="h2"
            className="text-zinc-900 text-3xl font-bold leading-tight"
            delay={0.2}
          >
            {t('auth.welcomeBack')}
          </MotionText>
        </div>
        <motion.div className='my-6 flex flex-col gap-4'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: "easeInOut"
          }}
        >
          <EmailInput
            name='email'
            autoComplete='email'
            label={t('auth.yourEmail')}
            required
          />
          <PasswordInput
            name='password'
            autoComplete="current-password"
            label={t('auth.yourPassword')}
            required
            error={error?.message}
          />
        </motion.div>
        <Link
          to={'/password/forgot'}
          className='text-zinc-700 text-xs text-center'>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeInOut"
            }}
          >
            {t('auth.forgotPassword')} <u>{t('auth.forgotPasswordReset')}</u>
          </motion.span>
        </Link>
      </div>

      <div className="flex flex-row gap-4">
        <Button type='submit' isLoading={isLoading}>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeInOut"
            }}
          >
            {t('common.login')}
          </motion.span>
        </Button>
      </div>
    </form>
  )
}

export default UserLoginPage